import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Card, Grid, Paper, Typography } from "@mui/material";
import axios from "../../api";
import { useParams } from "react-router-dom";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import { useCustomerId } from "context/CustomerIdContext";
import SoftBox from "components/SoftBox";
import { AxiosError } from "axios";
import { Inventory2Outlined,Add as AddIcon } from "@mui/icons-material";
import moment from "moment";

interface ViewAssetsProps {
  /** When provided, "Add Asset" closes current overlay and parent opens Add Asset overlay with customer pre-selected */
  onOpenAddAsset?: (customerId: number, customerType: string) => void;
  /** Pass when in overlay so Add Asset form gets company vs customer */
  customerType?: string;
  /** Increment to refetch the list without changing customer id */
  refreshNonce?: number;
}

interface Assets {
  asset_id: number;
  asset_name: string;
  asset_sku: string;
  asset_type_name: string;
  asset_purchased_date: string;
  asset_status: string;
  is_active: string | number;
}

const getStatusLabel = (assetStatus: string): string => {
  if (assetStatus === "installed_no_subscription") {
    return "Installed – No Subscription";
  }
  if (assetStatus === "in_use") {
    return "In Use";
  }
  return assetStatus || "N/A";
};

const getActiveLabel = (isActive: string | number): string => {
  return isActive === 1 ? "active" : "inactive";
};

const ViewAssets = ({
  onOpenAddAsset,
  customerType,
  refreshNonce = 0,
}: ViewAssetsProps = {}) => {
  const { fetchError } = useSnackbar();
  const contextId = useCustomerId();
  const { id: paramId } = useParams();
  const id = contextId ?? paramId;

  const [assets, setAssets] = useState<Assets[] | null>(null);

  const fetchAssets = useCallback(async () => {
    try {
      const response = await axios.get(`/assets/customer/${id}`);
      setAssets(response?.data?.list);
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets, refreshNonce]);

  return (
    <Card sx={{ p: 3, maxWidth: "100%", background: "#fff" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5" fontWeight={700}>
          Manage Assets
        </Typography>
        {onOpenAddAsset && id && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => onOpenAddAsset(Number(id), customerType ?? "customer")}
            sx={{
              backgroundColor: "#4aa4a4",
              textTransform: "none",
              borderRadius: "8px",
              "&:hover": { backgroundColor: "#3c8f8f" },
            }}
          >
            Add Asset
          </Button>
        )}
      </Box>

      <SoftBox mt={2}>
        <Grid container justifyContent="center">
          <Grid item xs={12} md={12}>
            {assets && assets.length > 0 ? (
              assets?.map((data) => {
                const formattedDate = data.asset_purchased_date
                  ? moment(data.asset_purchased_date).format("DD MMM YYYY")
                  : "N/A";
                const statusLabel = getStatusLabel(data.asset_status);
                const activeLabel = getActiveLabel(data.is_active);

                return (
                  <Paper
                    key={data.asset_id}
                    elevation={0}
                    sx={{
                      p: 2.5,
                      mb: 3,
                      borderRadius: "14px",
                      border: "1px solid rgba(224,224,224,0.45)",
                      backgroundColor: "#FAFAFA",
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Box display="flex" alignItems="center" gap={1.2}>
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: "50%",
                            backgroundColor: "#FFF7E6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#D99A32",
                          }}
                        >
                          <Inventory2Outlined sx={{ fontSize: 20 }} />
                        </Box>
                        <Box>
                          <Typography
                            fontSize={17}
                            fontWeight={700}
                            color="#222"
                            sx={{ lineHeight: "20px" }}
                          >
                            {data.asset_name}
                          </Typography>

                          <Typography
                            fontSize={13}
                            fontWeight={500}
                            mt={0.5}
                            color="#777"
                          >
                            {data.asset_sku}
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          px: 1.4,
                          py: 0.5,
                          borderRadius: "20px",
                          fontSize: "12px",
                          fontWeight: 600,
                          textTransform: "capitalize",
                          backgroundColor:
                            activeLabel === "active" ? "#E3FCEF" : "#F1F1F1",
                          color: activeLabel === "active" ? "#0A7A3D" : "#444",
                        }}
                      >
                        {activeLabel}
                      </Box>
                    </Box>
                    <Grid container spacing={2} mt={1.5}>
                      <Grid item xs={12} sm={4}>
                        <TextRow
                          label="Asset Type"
                          value={data.asset_type_name}
                        />
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <TextRow
                          label="Asset Purchased Date"
                          value={formattedDate}
                        />
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <TextRow label="Asset Status" value={statusLabel} />
                      </Grid>
                    </Grid>
                  </Paper>
                );
              })
            ) : (
              <Typography
                fontSize={15}
                fontWeight={600}
                color="#777"
                textAlign="center"
                mt={4}
              >
                No assets found
              </Typography>
            )}
          </Grid>
        </Grid>
      </SoftBox>
    </Card>
  );
};

const TextRow = ({ label, value }: any) => (
  <Box>
    <Typography fontSize={13} fontWeight={600} color="#828282">
      {label}
    </Typography>
    <Typography fontSize={15} mt={0.3} fontWeight={600} color="#222">
      {value}
    </Typography>
  </Box>
);

export default ViewAssets;
